import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  Store, Network, Barcode, UtensilsCrossed, Monitor, Globe,
  CreditCard, Package, BarChart3, ShieldCheck, Workflow, Bell,
  Smartphone, Receipt, ShoppingBag,
} from "lucide-react";
import { ServicePage, type ServicePageData } from "@/components/sections/service-page";

const CHILDREN: Record<string, ServicePageData & { metaDesc: string }> = {
  "single-store-pos": {
    eyebrow: "POS · Retail",
    title: "Single-store POS",
    sub: "Fast, reliable checkout for one location — set up in days.",
    metaDesc: "Single-store POS: lightning checkout, integrated payments, inventory and offline mode for one retail location.",
    intro:
      "Built for shops, boutiques and specialty retailers running one location. Lightning-fast checkout, integrated card payments, real-time inventory and full offline mode — on the hardware you already have or a turnkey kit from us.",
    features: [
      { icon: ShoppingBag, title: "Lightning checkout", description: "Barcode scan, quick keys and saved carts." },
      { icon: CreditCard, title: "Integrated payments", description: "Card, contactless and wallets in one tap." },
      { icon: Package, title: "Live inventory", description: "Stock decrements as you sell, with low-stock alerts." },
      { icon: Receipt, title: "Receipts & invoices", description: "Branded print, email and SMS receipts." },
      { icon: Smartphone, title: "Offline mode", description: "Keep selling when Wi-Fi drops, sync on reconnect." },
      { icon: BarChart3, title: "Daily reports", description: "Sales, staff and cash-up reports out of the box." },
    ],
    process: [
      { step: "01", title: "Catalog", description: "Import products, categories, taxes and prices." },
      { step: "02", title: "Hardware", description: "Tablet, printer, cash drawer and card reader." },
      { step: "03", title: "Train", description: "Staff training and a half-day pilot." },
      { step: "04", title: "Go live", description: "Full launch with day-one support." },
    ],
    deliverables: ["Configured POS", "Hardware kit", "Catalog import", "Payment integration", "Staff training"],
    techStack: ["Custom POS (React + Capacitor)", "Stripe Terminal", "Square", "Supabase"],
    pricing: [
      { name: "DIY", price: "$1k", features: ["Software setup", "Catalog import", "Self-install hardware"] },
      { name: "Turnkey", price: "$2.5k", period: "setup", highlighted: true, features: ["Hardware kit", "Onsite install", "Staff training"] },
      { name: "Premium", price: "$4k", features: ["Custom branding", "Loyalty + gift cards", "Priority support"] },
    ],
    faqs: [
      { q: "Can we use our iPad?", a: "Yes — any iPad from 2018+ works perfectly." },
      { q: "What about taxes?", a: "Configured per region with automatic VAT/sales-tax calculation." },
    ],
  },
  "multi-branch-pos": {
    eyebrow: "POS · Retail",
    title: "Multi-branch POS",
    sub: "Centralized inventory, pricing and reporting across every store.",
    metaDesc: "Multi-branch POS: centralized catalog, inter-branch stock transfers, role-based access and consolidated reporting.",
    intro:
      "Run 2 to 200 locations from one dashboard. Shared catalog, branch-specific pricing, stock transfers, staff roles and consolidated reporting — with each store fully offline-capable.",
    features: [
      { icon: Network, title: "Central catalog", description: "Edit once, push to every branch instantly." },
      { icon: Package, title: "Stock transfers", description: "Move stock between locations with full audit trail." },
      { icon: ShieldCheck, title: "Role-based access", description: "Manager, cashier and admin permissions per branch." },
      { icon: BarChart3, title: "Consolidated reports", description: "Sales by branch, region, staff and product." },
      { icon: Bell, title: "Low-stock alerts", description: "Per-branch reorder points and notifications." },
      { icon: Workflow, title: "Branch pricing", description: "Override prices and promos per location." },
    ],
    process: [
      { step: "01", title: "Setup HQ", description: "Central catalog, taxes, roles and approvals." },
      { step: "02", title: "Onboard branches", description: "Per-branch hardware, staff and pilots." },
      { step: "03", title: "Migrate", description: "Existing data, customers and gift cards." },
      { step: "04", title: "Roll out", description: "Phased launch with HQ command center." },
    ],
    deliverables: ["Central catalog", "Per-branch setup", "Transfer workflow", "Role matrix", "Multi-branch dashboards"],
    techStack: ["Custom POS", "Stripe Terminal", "Supabase", "Metabase"],
    pricing: [
      { name: "Small chain", price: "$6k", features: ["Up to 3 branches", "Central reporting", "Stock transfers"] },
      { name: "Growing chain", price: "$15k", period: "setup", highlighted: true, features: ["Up to 10 branches", "Loyalty", "Custom dashboards"] },
      { name: "Enterprise", price: "Custom", features: ["Unlimited branches", "ERP integration", "Dedicated CSM"] },
    ],
    faqs: [
      { q: "Can branches set their own prices?", a: "Yes — HQ sets defaults, branches can override within limits." },
      { q: "Does it work offline per branch?", a: "Yes — each branch is fully offline-capable with auto-sync." },
    ],
  },
  "barcode-inventory": {
    eyebrow: "POS · Retail",
    title: "Barcode & Inventory",
    sub: "Real-time stock control with barcode and label workflows.",
    metaDesc: "Barcode and inventory module for POS: barcode generation, scanning, stock counts, lots and serial tracking.",
    intro:
      "Print labels, scan barcodes, run cycle counts and trace lots end-to-end — directly from the POS or a handheld scanner.",
    features: [
      { icon: Barcode, title: "Barcode generation", description: "EAN, UPC, Code-128 — print to Zebra or Brother." },
      { icon: Package, title: "Lot & serial", description: "Full traceability for expiring or serialized goods." },
      { icon: ShieldCheck, title: "Cycle counts", description: "Scheduled and ad-hoc counts with variance reports." },
      { icon: Bell, title: "Low-stock alerts", description: "Per-SKU reorder points and notifications." },
      { icon: BarChart3, title: "Stock valuation", description: "FIFO, weighted average and standard costing." },
      { icon: Workflow, title: "Receiving", description: "PO ↔ receipt matching with damage logging." },
    ],
    process: [
      { step: "01", title: "Audit", description: "Existing SKUs, barcodes and counting workflow." },
      { step: "02", title: "Setup", description: "Reorder rules, lots, labels and scanners." },
      { step: "03", title: "First count", description: "Full baseline count with reconciliation." },
      { step: "04", title: "Operate", description: "Daily scanning + monthly cycle counts." },
    ],
    deliverables: ["Barcode strategy", "Label templates", "Scanner config", "Count plan", "Variance reports"],
    techStack: ["Zebra scanners", "Brother label printers", "Custom POS", "Supabase"],
    pricing: [
      { name: "Setup", price: "$2k", features: ["Labels + scanners", "Up to 2k SKUs", "Training"] },
      { name: "Pro", price: "$5k", period: "setup", highlighted: true, features: ["Lots/serial", "Cycle counts", "Reorder automation"] },
      { name: "Enterprise", price: "Custom", features: ["WMS integration", "Multi-warehouse", "Advanced costing"] },
    ],
    faqs: [
      { q: "Do you supply scanners?", a: "Yes — Zebra, Honeywell or bring-your-own Bluetooth." },
      { q: "Can we track expiry dates?", a: "Yes — with lot tracking and FEFO picking." },
    ],
  },
  "table-management": {
    eyebrow: "POS · Restaurant",
    title: "Table Management",
    sub: "Visual floor plans, course timing and split-check flows.",
    metaDesc: "Restaurant POS table management: floor plan, course timing, modifiers, split checks and tipping workflows.",
    intro:
      "Built for front-of-house. Drag-and-drop floor plans, course timing, split checks, tipping and modifiers — designed to speed up service and improve average ticket.",
    features: [
      { icon: UtensilsCrossed, title: "Floor plan", description: "Drag-and-drop tables, sections and statuses." },
      { icon: Workflow, title: "Course timing", description: "Send drinks, apps, mains and desserts on cue." },
      { icon: Receipt, title: "Split checks", description: "Split by seat, item or evenly with a tap." },
      { icon: CreditCard, title: "Tipping flows", description: "Suggested tips, tip-out and reporting." },
      { icon: Bell, title: "Modifiers", description: "Required, optional, conditional and 86'd items." },
      { icon: BarChart3, title: "Server reports", description: "Sales per server, table turn time and avg ticket." },
    ],
    process: [
      { step: "01", title: "Map the floor", description: "Sections, capacity and reservation rules." },
      { step: "02", title: "Menu setup", description: "Categories, courses, modifiers and 86 list." },
      { step: "03", title: "Train", description: "Server walkthrough and live shift." },
      { step: "04", title: "Iterate", description: "Tune timings, reports and screens." },
    ],
    deliverables: ["Floor plan", "Menu + modifiers", "Server roles", "Tipping config", "FOH reports"],
    techStack: ["Custom POS", "Stripe Terminal", "Toast APIs", "Supabase"],
    pricing: [
      { name: "Cafe", price: "$2.5k", features: ["Up to 20 tables", "Standard menu", "Splits + tips"] },
      { name: "Full-service", price: "$6k", period: "setup", highlighted: true, features: ["Multi-section", "Course timing", "Server analytics"] },
      { name: "Group", price: "Custom", features: ["Multi-venue", "Centralized menu", "Manager dashboards"] },
    ],
    faqs: [
      { q: "Do you integrate with reservations?", a: "Yes — OpenTable, SevenRooms and custom systems." },
      { q: "Tip-out by role?", a: "Yes — fully configurable across servers, bar, kitchen and bussers." },
    ],
  },
  "kitchen-display": {
    eyebrow: "POS · Restaurant",
    title: "Kitchen Display (KDS)",
    sub: "Digital tickets, course routing and bump-bar speed for the line.",
    metaDesc: "Kitchen Display System (KDS): digital tickets, course routing, station screens, bump bars and prep timers.",
    intro:
      "Replace paper tickets with a fast, color-coded KDS. Route by station and course, prioritize by promise time, and measure prep speed station-by-station.",
    features: [
      { icon: Monitor, title: "Station screens", description: "Per-station displays with course routing." },
      { icon: Workflow, title: "Bump bar support", description: "Industrial bump bars for instant ticket flow." },
      { icon: Bell, title: "Prep timers", description: "Per-item timers with color-coded alerts." },
      { icon: ShieldCheck, title: "Recall & 86", description: "Recall sent items, mark 86'd in one tap." },
      { icon: BarChart3, title: "Speed reports", description: "Average ticket time per station and shift." },
      { icon: Workflow, title: "Online order sync", description: "Delivery orders land straight on the line." },
    ],
    process: [
      { step: "01", title: "Map stations", description: "Cold, hot, expo, bar and pass routing." },
      { step: "02", title: "Hardware", description: "Screens, mounts, bump bars and PoE switches." },
      { step: "03", title: "Pilot service", description: "One service with chef and dev side-by-side." },
      { step: "04", title: "Tune", description: "Routing, timers and alerts dialed in." },
    ],
    deliverables: ["Station routing", "KDS install", "Bump bar config", "Prep-time benchmarks"],
    techStack: ["Custom KDS", "Industrial Android", "Logic Controls bump bars"],
    pricing: [
      { name: "Single station", price: "$1.5k", features: ["1 screen", "Basic routing", "Standard mount"] },
      { name: "Full line", price: "$5k", period: "setup", highlighted: true, features: ["Up to 4 stations", "Bump bars", "Speed reports"] },
      { name: "Multi-venue", price: "Custom", features: ["Central config", "Custom hardware", "Onsite install"] },
    ],
    faqs: [
      { q: "Can we use existing TVs?", a: "Yes if they're HDMI — we recommend industrial-grade for kitchens." },
      { q: "Does it route delivery tickets?", a: "Yes — DoorDash, Uber Eats and direct orders land on the line." },
    ],
  },
  "online-orders": {
    eyebrow: "POS · Restaurant",
    title: "Online Orders",
    sub: "Direct online ordering that lands on your POS, not a marketplace.",
    metaDesc: "Online ordering for restaurants: branded site, menu sync, delivery integrations, pickup and direct POS injection.",
    intro:
      "Own your online channel with a branded ordering site that syncs your POS menu and routes orders straight to the KDS — plus DoorDash, Uber Eats and Deliveroo aggregation in one inbox.",
    features: [
      { icon: Globe, title: "Branded site", description: "Custom domain, photos and your full menu." },
      { icon: Workflow, title: "Menu sync", description: "Edit the menu in POS, online updates instantly." },
      { icon: Bell, title: "POS injection", description: "Orders flow to KDS — no tablet farm needed." },
      { icon: CreditCard, title: "Payments", description: "Card, Apple Pay, Google Pay and wallets." },
      { icon: Workflow, title: "Aggregator inbox", description: "DoorDash, Uber Eats, Deliveroo in one screen." },
      { icon: BarChart3, title: "Order analytics", description: "Channel, time-of-day and basket-size reports." },
    ],
    process: [
      { step: "01", title: "Brand & menu", description: "Site, photos, modifiers and prep times." },
      { step: "02", title: "Integrate POS/KDS", description: "Direct injection, no manual re-entry." },
      { step: "03", title: "Launch", description: "Local SEO + Google Business setup." },
      { step: "04", title: "Optimize", description: "Conversion tuning and upsell flows." },
    ],
    deliverables: ["Branded ordering site", "Menu sync", "Aggregator inbox", "Payment integration", "Order analytics"],
    techStack: ["Next.js", "Stripe", "Otter / Cuboh", "Custom POS", "Supabase"],
    pricing: [
      { name: "Starter", price: "$2k", features: ["Branded site", "POS sync", "Card payments"] },
      { name: "Growth", price: "$6k", period: "setup", highlighted: true, features: ["Aggregator inbox", "Loyalty", "Custom domain + SEO"] },
      { name: "Enterprise", price: "Custom", features: ["Multi-venue", "Native apps", "Marketing automation"] },
    ],
    faqs: [
      { q: "Do we still need DoorDash?", a: "Most clients keep aggregators for reach and use direct ordering for margin." },
      { q: "Can customers schedule orders?", a: "Yes — pickup or delivery, hours and days ahead." },
    ],
  },
};

export const Route = createFileRoute("/software/pos/$child")({
  loader: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) throw notFound();
    return { item, slug: params.child };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { item } = loaderData;
    const url = `/software/pos/${params.child}`;
    return {
      meta: [
        { title: `${item.title} — POS | CoderLity` },
        { name: "description", content: item.metaDesc },
        { property: "og:title", content: `${item.title} — POS | CoderLity` },
        { property: "og:description", content: item.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: PosChild,
});

function PosChild() {
  const { item } = Route.useLoaderData();
  return <ServicePage data={item} />;
}
